import { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, Edit2, Home, MapPin, Plus, Trash2, X,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  useAddresses, useDeleteAddress, useDeleteStorageLocation,
  useSaveAddress, useSaveStorageLocation, useStorageLocations,
  type Address, type StorageLocation,
} from './useLocations';

const SL_TYPES = [
  { value: 'container', label: 'Contenedor (cava, freezer, mueble)' },
  { value: 'shelf', label: 'Estante / Pila' },
  { value: 'position', label: 'Posición / Slot' },
  { value: 'custom', label: 'Otro' },
];

export function LocationsPage() {
  const { data: addresses } = useAddresses();
  const { data: allLocations } = useStorageLocations();
  const saveAddress = useSaveAddress();
  const deleteAddress = useDeleteAddress();
  const saveLocation = useSaveStorageLocation();
  const deleteLocation = useDeleteStorageLocation();

  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [editingAddress, setEditingAddress] = useState<Address | 'new' | null>(null);

  // Cuando hay un addressId vacío, elegimos el primero por default.
  const effectiveAddressId =
    selectedAddressId ?? (addresses && addresses.length > 0 ? addresses[0].id : null);

  // Locations del address seleccionado, agrupadas por parent_id para renderizar el árbol.
  const locationsByParent = useMemo(() => {
    const m = new Map<number | null, StorageLocation[]>();
    (allLocations ?? [])
      .filter((l) => l.address_id === effectiveAddressId)
      .forEach((l) => {
        const arr = m.get(l.parent_id) ?? [];
        arr.push(l);
        m.set(l.parent_id, arr);
      });
    return m;
  }, [allLocations, effectiveAddressId]);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <header className="mb-4">
        <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2">
          <MapPin size={22} className="text-notion-500" /> Lugares de guardado
        </h1>
        <p className="text-sm text-notion-500">
          Definí dónde guardás tus vinos: domicilios (Casa, Quincho, Cava externa) y ubicaciones
          jerárquicas dentro de cada uno (Cava → Pila A → Estante 3).
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {/* COLUMNA IZQUIERDA: domicilios */}
        <div className="card p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-notion-500">Domicilios</p>
            <button
              type="button"
              onClick={() => setEditingAddress('new')}
              className="text-emerald-700 hover:text-emerald-800"
              aria-label="Nuevo domicilio"
            >
              <Plus size={14} />
            </button>
          </div>

          {!addresses || addresses.length === 0 ? (
            <p className="text-xs text-notion-500 py-3 text-center">
              Aún no creaste ningún domicilio.
            </p>
          ) : (
            <ul className="space-y-1">
              {addresses.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedAddressId(a.id)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 rounded inline-flex items-center gap-2 text-sm',
                      a.id === effectiveAddressId
                        ? 'bg-emerald-50 text-emerald-900 font-medium'
                        : 'text-notion-700 hover:bg-notion-50',
                    )}
                  >
                    <Home size={13} className="text-notion-500 shrink-0" />
                    <span className="truncate flex-1">{a.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* COLUMNA DERECHA: árbol de ubicaciones */}
        <div className="card p-3 md:col-span-2">
          {effectiveAddressId === null ? (
            <p className="text-sm text-notion-500 py-6 text-center">
              Creá un domicilio para empezar a definir ubicaciones.
            </p>
          ) : (
            <>
              <AddressHeader
                address={addresses!.find((a) => a.id === effectiveAddressId)!}
                onEdit={() =>
                  setEditingAddress(addresses!.find((a) => a.id === effectiveAddressId)!)
                }
                onDelete={async () => {
                  if (
                    confirm(
                      '¿Eliminar este domicilio y todas sus ubicaciones? Las botellas asociadas quedarán sin lugar.',
                    )
                  ) {
                    await deleteAddress.mutateAsync(effectiveAddressId);
                    setSelectedAddressId(null);
                  }
                }}
              />

              <TreeBranch
                parentId={null}
                addressId={effectiveAddressId}
                locationsByParent={locationsByParent}
                onSave={({ id, ...payload }) =>
                  saveLocation.mutateAsync({ id, payload })
                }
                onDelete={(id) => deleteLocation.mutateAsync(id)}
              />
            </>
          )}
        </div>
      </div>

      {/* Modal de address (nuevo / editar) */}
      {editingAddress !== null && (
        <AddressDialog
          address={editingAddress === 'new' ? null : editingAddress}
          onClose={() => setEditingAddress(null)}
          onSave={async (payload) => {
            const saved = await saveAddress.mutateAsync({
              id: editingAddress === 'new' ? undefined : editingAddress.id,
              payload,
            });
            setSelectedAddressId(saved.id);
            setEditingAddress(null);
          }}
        />
      )}
    </div>
  );
}

function AddressHeader({
  address, onEdit, onDelete,
}: {
  address: Address;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between mb-3 pb-3 border-b border-notion-100">
      <div className="min-w-0">
        <p className="text-base font-medium">{address.label}</p>
        {(address.street || address.city || address.country) && (
          <p className="text-xs text-notion-500">
            {[address.street, address.city, address.country].filter(Boolean).join(', ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="text-notion-400 hover:text-notion-800 p-1"
          aria-label="Editar domicilio"
        >
          <Edit2 size={13} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-notion-400 hover:text-red-600 p-1"
          aria-label="Eliminar domicilio"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function TreeBranch({
  parentId, addressId, locationsByParent, onSave, onDelete,
}: {
  parentId: number | null;
  addressId: number;
  locationsByParent: Map<number | null, StorageLocation[]>;
  onSave: (payload: Partial<StorageLocation> & { id?: number }) => Promise<StorageLocation>;
  onDelete: (id: number) => Promise<void>;
}) {
  const children = locationsByParent.get(parentId) ?? [];
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<'container' | 'shelf' | 'position' | 'custom'>('shelf');

  const createChild = async () => {
    if (!newLabel.trim()) return;
    await onSave({
      address_id: addressId,
      parent_id: parentId,
      type: newType,
      label: newLabel.trim(),
    });
    setNewLabel('');
    setAdding(false);
  };

  return (
    <div className="space-y-1">
      {children.length === 0 && parentId === null && (
        <p className="text-xs text-notion-500 py-3 text-center">
          Aún no hay ubicaciones. Agregá la primera con el botón "+".
        </p>
      )}

      <ul className={cn(parentId !== null && 'ml-4 border-l border-notion-100 pl-3')}>
        {children.map((loc) => (
          <TreeNode
            key={loc.id}
            location={loc}
            addressId={addressId}
            locationsByParent={locationsByParent}
            onSave={onSave}
            onDelete={onDelete}
          />
        ))}

        {adding ? (
          <li className="ml-4 mt-2 flex items-center gap-1.5">
            <input
              className="input py-1 text-sm flex-1"
              placeholder="ej. Pila A, Estante 3, Posición 5"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              autoFocus
            />
            <select
              className="input py-1 text-xs"
              value={newType}
              onChange={(e) =>
                setNewType(e.target.value as 'container' | 'shelf' | 'position' | 'custom')
              }
            >
              {SL_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={createChild}
              className="text-emerald-700 hover:text-emerald-800 p-1"
            >
              <Plus size={14} />
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-notion-400 hover:text-notion-800 p-1"
            >
              <X size={14} />
            </button>
          </li>
        ) : (
          <li className={cn('mt-1.5', parentId !== null && 'ml-4')}>
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
            >
              <Plus size={11} /> {parentId === null ? 'Agregar ubicación raíz' : 'Agregar dentro'}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

function TreeNode({
  location, addressId, locationsByParent, onSave, onDelete,
}: {
  location: StorageLocation;
  addressId: number;
  locationsByParent: Map<number | null, StorageLocation[]>;
  onSave: (payload: Partial<StorageLocation> & { id?: number }) => Promise<StorageLocation>;
  onDelete: (id: number) => Promise<void>;
}) {
  const children = locationsByParent.get(location.id) ?? [];
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(location.label);

  return (
    <li className="py-0.5">
      <div className="flex items-center gap-1 group">
        {children.length > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-notion-400 hover:text-notion-800"
            aria-label={expanded ? 'Colapsar' : 'Expandir'}
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className="w-3" />
        )}

        {editing ? (
          <>
            <input
              className="input py-0.5 text-sm flex-1"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              onClick={async () => {
                await onSave({ id: location.id, label });
                setEditing(false);
              }}
              className="text-emerald-700 p-0.5"
            >
              <Plus size={12} className="rotate-45" />
            </button>
            <button
              type="button"
              onClick={() => {
                setLabel(location.label);
                setEditing(false);
              }}
              className="text-notion-400 p-0.5"
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <>
            <span className="text-sm flex-1 truncate">{location.label}</span>
            <span className="text-[10px] text-notion-400">{location.type_label}</span>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-notion-300 hover:text-notion-700 p-0.5 opacity-0 group-hover:opacity-100"
            >
              <Edit2 size={11} />
            </button>
            <button
              type="button"
              onClick={async () => {
                if (children.length > 0) {
                  alert('No se puede eliminar: tiene ubicaciones hijas. Eliminá primero las internas.');
                  return;
                }
                if (confirm(`¿Eliminar la ubicación "${location.label}"?`)) {
                  await onDelete(location.id);
                }
              }}
              className="text-notion-300 hover:text-red-600 p-0.5 opacity-0 group-hover:opacity-100"
            >
              <Trash2 size={11} />
            </button>
          </>
        )}
      </div>

      {expanded && (children.length > 0 || true) && (
        <TreeBranch
          parentId={location.id}
          addressId={addressId}
          locationsByParent={locationsByParent}
          onSave={onSave}
          onDelete={onDelete}
        />
      )}
    </li>
  );
}

function AddressDialog({
  address, onClose, onSave,
}: {
  address: Address | null;
  onClose: () => void;
  onSave: (payload: Partial<Address>) => Promise<void>;
}) {
  const [label, setLabel] = useState(address?.label ?? '');
  const [street, setStreet] = useState(address?.street ?? '');
  const [city, setCity] = useState(address?.city ?? '');
  const [country, setCountry] = useState(address?.country ?? 'Argentina');
  const [notes, setNotes] = useState(address?.notes ?? '');

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-md w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-medium text-notion-800">
          {address ? 'Editar domicilio' : 'Nuevo domicilio'}
        </p>

        <div>
          <label className="label">Nombre / etiqueta</label>
          <input
            className="input"
            placeholder="ej. Casa, Quincho, Cava de Diego"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Calle</label>
            <input
              className="input"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Ciudad</label>
            <input
              className="input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="label">País</label>
          <input
            className="input"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Notas</label>
          <textarea
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" className="btn-secondary flex-1" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            disabled={!label.trim()}
            onClick={() =>
              onSave({
                label: label.trim(),
                street: street.trim() || null,
                city: city.trim() || null,
                country: country.trim() || null,
                notes: notes.trim() || null,
              })
            }
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
